/**
 * UpdateSettingsPanel — Settings → General "App Updates" section (Phase 18).
 *
 * Companion to UpdateNotification (the toast). Where the toast is reactive
 * (only shows when something interesting happens), this panel is always
 * present in Settings and gives the user:
 *
 *   - Current version display
 *   - Live status from the updater ("Up to date" / "Downloading 42%" / etc.)
 *   - A "Check for updates" button (manual trigger)
 *   - A "Restart now" button when a download is ready
 *
 * Subscribes to the same `updater.onStatus` stream as the toast. Renders
 * a friendly placeholder in dev / non-Electron contexts so the section
 * isn't a confusing blank area during development.
 *
 * Props:
 *   currentVersion  — string, from package.json
 *   darkMode
 */

import { useEffect, useState } from 'react';
import { Download, RefreshCw, CheckCircle2, AlertCircle, Cloud } from 'lucide-react';

const isAvailable = () =>
  typeof window !== 'undefined' &&
  typeof window.electronAPI?.updater?.onStatus === 'function';

const fmtBytes = (n) => {
  if (!n || !Number.isFinite(n)) return '';
  if (n > 1e6) return `${(n / 1e6).toFixed(1)} MB/s`;
  if (n > 1e3) return `${(n / 1e3).toFixed(0)} KB/s`;
  return `${n} B/s`;
};

const UpdateSettingsPanel = ({ currentVersion = '', darkMode = true }) => {
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => {
    if (!isAvailable()) return undefined;
    return window.electronAPI.updater.onStatus(setStatus);
  }, []);

  const handleCheck = async () => {
    if (!isAvailable() || checking) return;
    setChecking(true);
    try {
      await window.electronAPI.updater.checkNow();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[UpdateSettings] check failed:', e?.message || e);
    } finally {
      // Brief minimum spinner so the user sees feedback even on a cached "up to date"
      setTimeout(() => setChecking(false), 800);
    }
  };

  const handleRestart = async () => {
    if (!isAvailable() || restarting) return;
    setRestarting(true);
    try {
      await window.electronAPI.updater.restartAndInstall();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[UpdateSettings] restart failed:', e?.message || e);
      setRestarting(false);
    }
  };

  const cardCls = darkMode
    ? 'bg-black/45 backdrop-blur-md border-white/10'
    : 'bg-white/20 backdrop-blur-xs border-white/20';
  const labelCls = darkMode ? 'text-gray-400' : 'text-gray-500';
  const valueCls = darkMode ? 'text-white' : 'text-gray-900';

  // ── Status line text + icon ─────────────────────────────────────────
  const renderStatusLine = () => {
    if (!isAvailable()) {
      return (
        <span className={`inline-flex items-center gap-1.5 text-xs ${labelCls}`}>
          <Cloud size={12} />
          Auto-updates not available in this build
        </span>
      );
    }
    if (!status) {
      return (
        <span className={`inline-flex items-center gap-1.5 text-xs ${labelCls}`}>
          <Cloud size={12} />
          Checking on launch…
        </span>
      );
    }
    if (status.status === 'dev') {
      return (
        <span className={`inline-flex items-center gap-1.5 text-xs italic ${labelCls}`}>
          <Cloud size={12} />
          Dev build — auto-updates disabled
        </span>
      );
    }
    if (status.status === 'checking') {
      return (
        <span className={`inline-flex items-center gap-1.5 text-xs ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
          <RefreshCw size={12} className="animate-spin" />
          Checking for updates…
        </span>
      );
    }
    if (status.status === 'up-to-date') {
      return (
        <span className={`inline-flex items-center gap-1.5 text-xs ${darkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
          <CheckCircle2 size={12} />
          You're on the latest version
        </span>
      );
    }
    if (status.status === 'downloading') {
      const pct = Math.max(0, Math.min(100, Math.round(status.percent || 0)));
      return (
        <span className={`inline-flex items-center gap-1.5 text-xs ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
          <Download size={12} className="animate-pulse" />
          Downloading v{status.latestVersion} — {pct}%{status.bytesPerSecond ? ` · ${fmtBytes(status.bytesPerSecond)}` : ''}
        </span>
      );
    }
    if (status.status === 'downloaded') {
      return (
        <span className={`inline-flex items-center gap-1.5 text-xs ${darkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
          <CheckCircle2 size={12} />
          v{status.latestVersion} downloaded — restart to install
        </span>
      );
    }
    if (status.status === 'error') {
      return (
        <span className={`inline-flex items-center gap-1.5 text-xs ${darkMode ? 'text-red-300' : 'text-red-700'}`} title={status.error}>
          <AlertCircle size={12} />
          Update check failed — {status.error?.slice(0, 80)}
        </span>
      );
    }
    if (status.status === 'unavailable') {
      return (
        <span className={`inline-flex items-center gap-1.5 text-xs ${labelCls}`}>
          <Cloud size={12} />
          Auto-updates not available in this build
        </span>
      );
    }
    return null;
  };

  const isDownloaded = status?.status === 'downloaded';
  const canCheck = isAvailable() && status?.status !== 'dev' && status?.status !== 'unavailable';

  return (
    <div className="mb-6">
      <h3 className={`text-lg font-semibold mb-3 transition-colors duration-300 ${valueCls}`}>
        App Updates
      </h3>
      <div className={`p-4 rounded-lg border ${cardCls}`}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className={`text-xs uppercase tracking-wide ${labelCls}`}>Current version</div>
            <div className={`font-mono text-sm mt-0.5 ${valueCls}`}>v{currentVersion || '—'}</div>
            <div className="mt-2">
              {renderStatusLine()}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isDownloaded ? (
              <button
                type="button"
                onClick={handleRestart}
                disabled={restarting}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-[background-color,border-color,box-shadow,color] duration-150 disabled:opacity-40 disabled:cursor-wait ${
                  darkMode
                    ? 'bg-emerald-500/25 border-emerald-400/50 text-emerald-50 ring-1 ring-inset ring-emerald-400/25 hover:bg-emerald-500/35 hover:border-emerald-400/70 hover:shadow-[0_0_16px_rgba(16,185,129,0.3)]'
                    : 'bg-emerald-100 border-emerald-300 text-emerald-800 hover:bg-emerald-200 hover:border-emerald-400'
                }`}
              >
                <RefreshCw size={13} className={restarting ? 'animate-spin' : ''} />
                {restarting ? 'Restarting…' : 'Restart now'}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCheck}
                disabled={!canCheck || checking}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-[background-color,border-color,box-shadow,color] duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${
                  darkMode
                    ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25 hover:bg-blue-500/35 hover:border-blue-400/70 hover:shadow-[0_0_16px_rgba(59,130,246,0.25)]'
                    : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200 hover:border-blue-400'
                }`}
              >
                <RefreshCw size={13} className={checking ? 'animate-spin' : ''} />
                {checking ? 'Checking…' : 'Check for updates'}
              </button>
            )}
          </div>
        </div>
        <p className={`text-[11px] mt-3 ${labelCls}`}>
          Journifi checks GitHub Releases automatically on launch and re-checks every 4 hours.
          When an update is ready, you'll see a toast in the corner — click Restart to apply it.
        </p>
      </div>
    </div>
  );
};

export default UpdateSettingsPanel;
